import React, { useState, useEffect } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../../../../Utils/interface";


const OrderRestrictionModal = ({ show, handleClose, errorData }) => {
  const navigate = useNavigate();
  const [enrichedData, setEnrichedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { error_code, data } = errorData || {};

  const getNumericId = (idOrCode) => {
    if (!idOrCode) return null;
    const match = String(idOrCode).match(/(\d+)/);
    return match ? match[1] : idOrCode;
  };

  const getOrderId = () => {
    if (data?.orderEntityId && data.orderEntityId !== 0) return data.orderEntityId;
    if (data?.entityid && data.entityid !== 0) return data.entityid;
    return getNumericId(data?.policyId || data?.code || data?.policyNumber);
  };

  const orderId = getOrderId();

  useEffect(() => {
    if (show && error_code === "ERR_022" && orderId) {
      setLoading(true);
      const fd = new FormData();
      fd.append("entityid", orderId);
      fd.append("entitytype", "ODR");

      api.post("/entity/read", fd)
        .then((res) => {
          if (res?.data) {
            setEnrichedData(res.data);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching order details in restriction modal:", err);
          setLoading(false);
        });
    } else {
      setEnrichedData(null);
    }
  }, [show, error_code, orderId]);

  if (!errorData) return null;

  const mergedData = { ...data, ...enrichedData };

  const getModalConfig = (mergedData) => {
    switch (error_code) {
      case "ERR_022":
        return {
          title: "Pending Order Found",
          description: "This customer already has a assisted order awaiting payment. Please continue with the existing order instead of creating a new one.",
          cardTitle: "Existing Pending Order",
          badgeText: "Pending",
          badgeColor: "#f39c12", // Orange
          badgeBg: "#fdf0d5",
          primaryBtnText: "Go to Existing Order",
          fields: [
            { label: "Order ID", value: `#${mergedData?.policyId || mergedData?.code || ''}`, link: `/order/orders/update/${orderId}` },
            { label: "Order Date", value: loading ? "Loading..." : (mergedData?.created_date ? moment(mergedData.created_date).format("DD/MM/YYYY") : (mergedData?.createdDate ? moment(mergedData.createdDate).format("DD/MM/YYYY") : "-")) },
            { label: "Product", value: mergedData?.product || mergedData?.name || "-" },
            { label: "Order Type", value: loading ? "Loading..." : (mergedData?.order_type === "FRESH" ? "Fresh Order" : (mergedData?.order_type || "-")) },
            { label: "Relationship Manager", value: loading ? "Loading..." : (`${mergedData?.rm_name || ''}`.trim() || 'N/A'), fullWidth: true }
          ]
        };
      case "ERR_023":
        return {
          title: "Active Policy Found",
          description: "This customer already has an active policy for this product, Please push for renewal",
          cardTitle: "Existing Active Order",
          badgeText: "Active",
          badgeColor: "#27ae60", // Green
          badgeBg: "#e8f8f5",
          primaryBtnText: "View Policy",
          fields: [
            { label: "Policy ID", value: `#${mergedData?.policyId}`, link: `/order/orders/update/${orderId}` },
            { label: "Product", value: mergedData?.product },
            { label: "Expiry Date", value: mergedData?.expiryDate ? moment(mergedData.expiryDate).format("DD MMM YYYY") : "-" }
          ]
        };
      case "ERR_024":
        return {
          title: "Grace Period Lapsed",
          description: "The customer's policy grace period has lapsed. Please create a fresh order.",
          cardTitle: "Lapsed Policy",
          badgeText: "Lapsed",
          badgeColor: "#c0392b", // Red
          badgeBg: "#f9ebea",
          primaryBtnText: null, // No primary action, only Cancel
          fields: [
            { label: "Policy Number", value: `#${mergedData?.policyNumber}` },
            { label: "Expired On", value: mergedData?.expiredOn ? moment(mergedData.expiredOn).format("DD/MM/YYYY") : "-" },
            { label: "Product", value: mergedData?.product },
            { label: "Grace Period Ends", value: mergedData?.gracePeriodEnds ? moment(mergedData.gracePeriodEnds).format("DD/MM/YYYY") : "-" }
          ]
        };
      case "ERR_025":
        return {
          title: "Policy in Grace Period",
          description: "The customer's policy has recently expired but is still within the renewal grace period.",
          cardTitle: "Existing Policy",
          badgeText: `Expiring in ${mergedData?.daysRemaining || 0} days`,
          badgeColor: "#e67e22", // Orange
          badgeBg: "#fdf0d5",
          primaryBtnText: "View Policy",
          footerText: "Renewing now will maintain policy continuity and benefits.",
          fields: [
            { label: "Policy Number", value: `#${mergedData?.policyNumber}`, link: `/order/orders/update/${orderId}` },
            { label: "Expired On", value: mergedData?.expiredOn ? moment(mergedData.expiredOn).format("DD/MM/YYYY") : "-" },
            { label: "Product", value: mergedData?.product },
            { label: "Grace Period Ends", value: mergedData?.gracePeriodEnds ? moment(mergedData.gracePeriodEnds).format("DD/MM/YYYY") : "-" }
          ]
        };
      default:
        return null;
    }
  };

  const config = getModalConfig(mergedData);

  if (!config) return null;

  const handlePrimaryClick = () => {
    const linkField = config.fields.find(f => f.link);
    if (linkField && linkField.link) {
      window.location.href = linkField.link;
    } else {
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      className="border-0"
      modalClassName="modal fade zoomIn"
      toggle={handleClose}
      size="md"
    >
      <ModalHeader
        toggle={handleClose}
        style={{ backgroundColor: "#fff3cd", borderBottom: "none" }}
      >
        <div className="d-flex align-items-center" style={{ color: "#856404", fontWeight: "bold" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#f1c40f" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          {config.title}
        </div>
      </ModalHeader>

      <ModalBody className="p-4 text-center">
        <p className="mb-4 text-muted" style={{ fontSize: "14px" }}>
          {config.description}
        </p>

        <div
          className="text-start p-3"
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            backgroundColor: "#fff"
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center font-weight-bold">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>{config.cardTitle}</span>
            </div>
            <span
              style={{
                backgroundColor: config.badgeBg,
                color: config.badgeColor,
                padding: "4px 8px",
                borderRadius: "16px",
                fontSize: "12px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center"
              }}
            >
              {error_code === "ERR_024" && (
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              )}
              {config.badgeText}
            </span>
          </div>

          <div className="row g-3">
            {config.fields.map((field, idx) => (
              <div key={idx} className={field.fullWidth ? "col-12" : "col-6"}>
                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px" }}>{field.label}</div>
                {field.link ? (
                  <div style={{ color: "#0056b3", cursor: "pointer", fontWeight: "500", textDecoration: "underline" }} onClick={() => { window.location.href = field.link; }}>
                    {field.value}
                  </div>
                ) : (
                  <div style={{ fontSize: "14px", fontWeight: "500", color: "#333" }}>{field.value}</div>
                )}
              </div>
            ))}
          </div>

          {config.footerText && (
            <div className="mt-3 pt-2 text-center" style={{ borderTop: "1px dashed #e0e0e0", fontSize: "12px", color: "#6c757d" }}>
              {config.footerText}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter className="border-0 justify-content-center pb-4 pt-0">
        <Button
          variant="outline-primary"
          onClick={handleClose}
          style={{ padding: "8px 24px", borderRadius: "6px" }}
        >
          Cancel
        </Button>
        {config.primaryBtnText && (
          <Button
            variant="primary"
            onClick={handlePrimaryClick}
            style={{ padding: "8px 24px", borderRadius: "6px" }}
          >
            {config.primaryBtnText}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default OrderRestrictionModal;
